/**
 * Utility functions for stock card rendering
 */

import { COLORS } from "../constants/colors";

// Constants for action card stacking
export const ACTION_CARD_HEADER_HEIGHT = 35;
export const ACTION_CARD_HEADER_OVERLAP = 0;

/**
 * Get color scheme for a stock sector
 */
export const getSectorColors = (sector = "") => {
  const s = sector.toLowerCase();
  
  if (s === "tech") {
    return {
      bg: "linear-gradient(145deg, #ffffff, #f0f5ff), linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.05) 100%)",
      border: "#3b82f6",
      header: "linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(37, 99, 235, 0.08))",
      headerBorder: "rgba(59, 130, 246, 0.3)"
    };
  }
  
  if (s === "finance") {
    return {
      bg: "linear-gradient(145deg, #ffffff, #f0fdf4), linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(22, 163, 74, 0.05) 100%)",
      border: "#22c55e",
      header: "linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(22, 163, 74, 0.08))",
      headerBorder: "rgba(34, 197, 94, 0.3)"
    };
  }
  
  if (s === "industrial") {
    return {
      bg: "linear-gradient(145deg, #ffffff, #faf5f0), linear-gradient(135deg, rgba(139, 69, 19, 0.08) 0%, rgba(101, 67, 33, 0.05) 100%)",
      border: "#8B4513",
      header: "linear-gradient(135deg, rgba(139, 69, 19, 0.12), rgba(101, 67, 33, 0.08))",
      headerBorder: "rgba(139, 69, 19, 0.3)"
    };
  }
  
  if (s === "crypto") {
    return {
      bg: "linear-gradient(145deg, #ffffff, #fff7ed), linear-gradient(135deg, rgba(255, 140, 0, 0.12) 0%, rgba(255, 107, 53, 0.08) 100%)",
      border: "#FF8C00",
      header: "linear-gradient(135deg, rgba(255, 140, 0, 0.18), rgba(255, 107, 53, 0.12))",
      headerBorder: "rgba(255, 140, 0, 0.4)"
    };
  }
  
  if (s === "health & science") {
    return {
      bg: "linear-gradient(145deg, #ffffff, #faf5ff), linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(147, 51, 234, 0.05) 100%)",
      border: COLORS.PRIMARY,
      header: "linear-gradient(135deg, rgba(168, 85, 247, 0.12), rgba(147, 51, 234, 0.08))",
      headerBorder: "rgba(168, 85, 247, 0.3)"
    };
  }
  
  // Default colors (if sector doesn't match)
  return {
    bg: `linear-gradient(145deg, #ffffff, #f0f0f0), ${COLORS.gradientPrimaryRgba(0.1, 0.1)}`,
    border: "#2c2c2c",
    header: COLORS.gradientPrimaryRgba(0.15, 0.15),
    headerBorder: COLORS.primaryRgba(0.3)
  };
};

/**
 * Get emoji for a stock sector
 */
export const getSectorEmoji = (sector = "") => {
  const s = sector.toLowerCase();
  if (s === "tech") return "💻";
  if (s === "finance") return "🏦";
  if (s === "industrial") return "🏭";
  if (s === "health & science") return "🧬";
  if (s === "crypto") return "₿";
  return "";
};

/**
 * Get color scheme for an action card
 */
export const getActionCardColors = (card) => {
  if (card.actionType === 'manipulate' || card.actionType === 'delayed_manipulate') {
    return {
      header: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.1))',
      border: '#f59e0b',
      emoji: card.actionType === 'delayed_manipulate' ? '⏰' : '📊'
    };
  } else if (card.actionType === 'forecast') {
    return {
      header: 'linear-gradient(135deg, rgba(147, 51, 234, 0.18), rgba(126, 34, 206, 0.12))',
      border: '#9333ea',
      emoji: '🤫'
    };
  } else if (card.actionType === 'shuffle') {
    return {
      header: 'linear-gradient(135deg, rgba(107, 114, 128, 0.15), rgba(75, 85, 99, 0.1))',
      border: '#6b7280',
      emoji: '🔀'
    };
  } else if (card.actionType === 'remove_manipulators') {
    return {
      header: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.1))',
      border: '#3b82f6',
      emoji: '⚖️'
    };
  }
  return {
    header: 'linear-gradient(135deg, rgba(156, 163, 175, 0.15), rgba(107, 114, 128, 0.1))',
    border: '#9ca3af',
    emoji: '🎴'
  };
};

/**
 * Format modifier display for action cards
 */
export const getModifierDisplay = (card) => {
  if (card.actionType === 'manipulate' || card.actionType === 'delayed_manipulate') {
    const value = card.effectValue || 0;
    return value > 0 ? `+${value}` : `${value}`;
  }
  return '';
};

